/*
 * Share Review Finder Layout Panel Template
 *
 * Includes:
 * - Landing Page for Shares Review page in finder layout
 */
import type {
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
  ShareReviewFinderPageArea,
} from '../types';
import {
  DETAILS_DEFAULT_WIDTH,
  DETAILS_MIN_WIDTH,
  SHARE_REVIEW_HEADER_PANEL_PROPS,
} from './constants';

/**
 * Base template (Mobile +)
 */
export function baseTemplate(): PanelsBreakpointTemplate {
  return {
    areas: [
      ['header', 'header'],
      ['directoryContent', 'viewer'],
      ['details', 'details'],
    ],
    resizers: {
      directoryContent: 'right',
      viewer: 'bottom',
      details: 'top',
    },
    columns: [
      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      /* Header Panel */
      {
        key: 'header',
        isToggleable: false,
        ...SHARE_REVIEW_HEADER_PANEL_PROPS,
      },
      /* Main Area */
      {
        key: 'directoryContent',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
      /* Details Panel */
      {
        key: 'details',
        defaultMin: 200,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        isToggleable: false,
        showing: false,
      },
    ],
  };
}

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [
      ['header', 'header', 'header'],
      ['directoryContent', 'viewer', 'details'],
    ],
    resizers: {
      directoryContent: 'right',
    },
    columns: [
      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Details Panel */
      {
        key: 'details',
        defaultMin: DETAILS_DEFAULT_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 400,
        isToggleable: true,
        showing: visible.includes('details'),
        isFlex: true,
      },
    ],
    rows: [
      /* Header Panel */
      {
        key: 'header',
        isToggleable: false,
        ...SHARE_REVIEW_HEADER_PANEL_PROPS,
      },
      /* Main area */
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

/**
 * Large template
 */

export const largeTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [
      ['header', 'header', 'header'],
      ['directoryContent', 'viewer', 'details'],
    ],
    resizers: {
      directoryContent: 'right',
    },
    columns: [
      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultMin: 376,
        defaultSize: '1fr',
        isToggleable: false,
        showing: false,
      },

      /* Details Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 600,
        isToggleable: true,
        showing: visible.includes('details'),
      },
    ],
    rows: [
      /* Header Panel */
      {
        key: 'header',
        isToggleable: false,
        ...SHARE_REVIEW_HEADER_PANEL_PROPS,
      },
      /* Main Area */
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels = (): Array<ShareReviewFinderPageArea> => {
  return ['header', 'directoryContent'];
};

// Because this page encapsulates three "pages", each has a different layout default
export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
